import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { ModuleEnum } from '../_utils/unums/module.enum';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserSimulationsService } from './user-simulations.service';

@Injectable({
  providedIn: 'root'
})
export class SimulationService {
  private readonly baseUrl: string;

  constructor(
    private firebaseService: FirebaseService,
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private userSimulationService: UserSimulationsService
  ) {
    this.baseUrl = `${environment.apiUrl}/simulations`;
  }

  public async getEnabledDemoSimulations(module: ModuleEnum, lang: string): Promise<any[]> {
    return await this.getActiveSimulations(module, lang,
      (simulation): boolean => simulation?.enabledDemo);
  }

  public async getActiveSimulations(module: ModuleEnum, lang: string, filter: (simulation: any) => boolean): Promise<any[]> {
    let simulations = await lastValueFrom(this.getModuleSimulations(module, [lang]));
    simulations = simulations.filter(x => x.visible);
    if (filter) {
      simulations = simulations.filter(simulation => filter(simulation));
    }
    return simulations;
  }

  public async getSimulationName(id: string): Promise<string> {
    const url = `${this.baseUrl}/${id}`;
    const simulation: any = await lastValueFrom(this.httpClient.get(url));
    return simulation.name;
  }

  public getSimulationById(id: string): Observable<any> {
    const url = `${this.baseUrl}/${id}`;
    return this.httpClient.get(url);
  }

  public async getSimulationMetadata(module: ModuleEnum, simKey: string): Promise<any> {
    return this.firebaseService.getDocument(`simulations_peers/${module}/${simKey}`);
  }

  public async getSimulationLevel(id: string): Promise<number> {
    const url = `${this.baseUrl}/${id}`;
    const simulation: any = await lastValueFrom(this.httpClient.get(url));
    return simulation.level;
  }

  public async getSimulationDetails(module: ModuleEnum, userId: string, isPeer: boolean = false): Promise<any[]> {
    const userSimulations = await lastValueFrom(this.userSimulationService.getUserSimulations({ userIds: [userId], modules: [module] }));
    const simulationRequests = [...new Set(
      userSimulations.map(s => s.simulationId)
    )].map(id => lastValueFrom(this.getSimulationById(id)));
    const simulations = await Promise.all(simulationRequests);
    const result = [];
    for (let i = 0; i < simulations.length; i++) {
      const simulation = simulations[i];
      result.push({
        simulationName: simulation.name[this.translateService.currentLang],
        simulationDescription: simulation.description[this.translateService.currentLang],
        simulationId: simulation.id,
        isOpen: false,
        index: i,
        attempts: userSimulations.filter(s => s.simulationId === simulation.id)
      });
    }
    return result;
  }

  public getModuleSimulations(module: ModuleEnum | string, langs: string[]): Observable<any[]> {
    const params: any = { module, langs };
    return this.httpClient.get<any[]>(this.baseUrl, { params });
  }

  public getSimulationPropertyById = (id: string, property: string): Observable<any> =>
    this.httpClient.get<any>(`${this.baseUrl}/${id}/property`, { params: { property } });

}
